import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Printer, Trophy } from "lucide-react";

const MEDALS = { 1: "text-amber-500", 2: "text-slate-400", 3: "text-orange-700" };

const label = (month) => {
  if (month === "all") return "All time";
  const [y, m] = month.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const Metric = ({ name, value, testid }) => (
  <div className="rounded-xl border border-border p-4" data-testid={testid}>
    <div className="overline">{name}</div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

/**
 * Admin → Doctor leaderboard: which referring doctor's poster actually brought
 * patients in this month, ranked on the money that was paid, with a printable
 * thank-you for the top few.
 */
export const DoctorLeaderboard = () => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [top, setTop] = useState("3");

  useEffect(() => {
    api
      .get("/admin/referral-sources/leaderboard", { params: month ? { month } : {} })
      .then(({ data: d }) => {
        setData(d);
        if (!month) setMonth(d.month);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [month]);

  const printThankYou = async () => {
    try {
      const { data: blob } = await api.get("/admin/referral-sources/thank-you.pdf", {
        params: { month: month || "all", top }, responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("Thank-you cards ready to print");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No activity to celebrate yet");
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const months = data?.months || [];

  return (
    <Card className="border-border" data-testid="doctor-leaderboard-tab">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl flex items-center gap-2 mb-1"><Trophy size={18} /> Doctor leaderboard</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Ranked on the value actually paid. Scans are counted from the month the poster was scanned.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Select value={month || "all"} onValueChange={setMonth}>
            <SelectTrigger className="w-52 h-10" data-testid="lb-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={top} onValueChange={setTop}>
            <SelectTrigger className="w-40 h-10" data-testid="lb-top"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["1", "3", "5", "10"].map((n) => <SelectItem key={n} value={n}>Top {n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="btn-pill h-10" onClick={printThankYou} data-testid="lb-thank-you">
            <Printer size={13} className="mr-1.5" /> Print thank-you cards
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <Metric name="Poster scans" value={totals.visits || 0} testid="lb-total-visits" />
          <Metric name="Prescriptions" value={totals.uploads || 0} testid="lb-total-uploads" />
          <Metric name="Orders paid" value={totals.paid_orders || 0} testid="lb-total-orders" />
          <Metric name="Value dispensed" value={`₹${Number(totals.revenue || 0).toFixed(0)}`} testid="lb-total-revenue" />
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="lb-empty">
            No referring doctors yet — add them under Referral sources and hand out their posters.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.code}
                className={`rounded-xl border p-4 transition-colors ${
                  row.rank === 1 ? "border-primary/50 bg-primary/5" : "border-border"
                }`}
                data-testid={`lb-row-${row.code}`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-8 text-center font-display text-xl" data-testid={`lb-rank-${row.code}`}>
                    {row.rank ? (
                      MEDALS[row.rank] ? <Award size={20} className={`mx-auto ${MEDALS[row.rank]}`} /> : row.rank
                    ) : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{row.doctor_name || row.code}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {row.location || "—"} · <span className="font-mono">{row.code}</span>
                      {!row.active && " · paused"}
                    </div>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline" data-testid={`lb-visits-${row.code}`}>{row.visits} scans</Badge>
                    <Badge variant="outline" data-testid={`lb-uploads-${row.code}`}>{row.uploads} Rx</Badge>
                    <Badge variant="outline" data-testid={`lb-paid-${row.code}`}>{row.paid_orders} paid</Badge>
                    <Badge variant="outline" data-testid={`lb-conv-${row.code}`}>{row.conversion}% converted</Badge>
                    <Badge className="bg-primary/10 text-primary border-primary/30" data-testid={`lb-revenue-${row.code}`}>
                      ₹{Number(row.revenue || 0).toFixed(0)}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!!totals.direct_uploads && (
          <p className="text-xs text-muted-foreground mt-5" data-testid="lb-direct">
            {totals.direct_uploads} prescription(s) came in without a referral code in {data?.month_label}.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DoctorLeaderboard;
